import { defineStore } from 'pinia';
import { ref, computed } from 'vue'
import { useBrowserStore } from './useBrowserStore';
import { API } from '~/utils/api';


export interface IAuthUser {
  access?: string;
  refresh?: string;
  email: string;
  role: number;
  role_name: string;
  id?: number;
  first_name: string;
  last_name: string;
  uid: string;
  created_at?: string;
  facility?: number;
  is_active?: boolean;
}

export interface LogonResult {
  access: string;
  refresh: string;
  email: string;
  role: number;
}

export const useAuthStore = defineStore('auth', () => {
  const browserStore = useBrowserStore()

  const userId = ref<number>(-1)
  const accessToken = ref<string>(browserStore.getFromCookie("token") || "")
  const refreshToken = ref<string>("")
  const loginData = ref({
    username: "",
    password: "",
    otp: ""
  })
  const userData = ref({
    uid: "",
    role: -1,
    role_name: "",
    email: "",
    lastName: "",
    firstName: ""
  })

  // Users list state
  const allUsers = ref<IAuthUser[]>([])
  const usersLoading = ref(false)

  const getToken = computed(() => accessToken.value || "")
  const getRole = computed(() => userData.value.role || -1)
  const isAuthenticated = computed(() => !!accessToken.value)


  async function getOTP(email: string, password: string, role: number) {
    return await API.post<LogonResult>(
      'user/otp_login',
      { email, password, role }
    )
  }

  async function verifyOTP(email: string, password: string, otp: string) {
    const result = await API.post<IAuthUser>(
      'user/otp_verify',
      { email, password, otp }
    )
    return result
  }

  async function fetchAllUsers() {
    usersLoading.value = true
    try {
      const result = await API.get<IAuthUser[]>('user/all/')
      if (result.success && result.data) {
        allUsers.value = result.data
      }
      return result
    } finally {
      usersLoading.value = false
    }
  }

  async function sendUserInvite(email: string, role: number, facility_id?: number) {
    const result = await API.post<any>(
      'user/invite/',
      { email, role, facility_id }
    )
    return result
  }

  async function resendUserInvite(uid: string) {
    const result = await API.post<any>(
      'user/invite/resend/',
      { uid }
    )
    return result
  }

  async function signUpUser(
    uidb64: string,
    token: string,
    password: string,
    first_name: string,
    last_name: string
  ) {
    const result = await API.post<any>(
      'user/signup/',
      { uidb64, token, password, first_name, last_name }
    )
    return result
  }

  async function updateUserRole(uid: string, role: number, facility_id?: number) {
    const result = await API.post<any>(
      `user/update-role/${uid}/`,
      { role, facility_id }
    )
    return result
  }

  async function deleteUser(uid: string) {
    const result = await API.deleteWithResult<any>(`user/delete/${uid}/`)
    return result
  }

  async function getUserDetails() {
    const result = await API.get<IAuthUser>('user/details/')
    return result
  }

  async function editUserDetails(first_name: string, last_name: string) {
    const result = await API.post<any>('user/edit/', { first_name, last_name })
    return result
  }

  async function forgotPassword(email: string) {
    const result = await API.post<any>('user/forgot-password/', { email })
    return result
  }

  async function resetPassword(uidb64: string, token: string, new_password: string) {
    const result = await API.post<any>('user/reset-password/', { uidb64, token, new_password })
    return result
  }

  function clearAll() {
    userId.value = -1
    accessToken.value = ""
    refreshToken.value = ""
    loginData.value = { username: "", password: "", otp: "" }
    userData.value = { uid: "", role: -1, role_name: "", email: "", lastName: "", firstName: "" }

    browserStore.removeFromLocal("token")
  }

  return {
    userId,
    accessToken,
    refreshToken,
    loginData,
    userData,
    allUsers,
    usersLoading,
    getToken,
    getRole,
    isAuthenticated,
    getOTP,
    verifyOTP,
    fetchAllUsers,
    sendUserInvite,
    resendUserInvite,
    signUpUser,
    clearAll,
    updateUserRole,
    deleteUser,
    getUserDetails,
    editUserDetails,
    forgotPassword,
    resetPassword
  }
})